import type { GalleryMediaT } from '../../../../../type/galleryMediaT';

import { useCarouselSync } from '@vs-shop/headless/useCarouselSync';
import {
  ProductGalleryLightboxMain,
  ProductGalleryLightboxTitle,
  ProductGalleryLightboxViewport,
  ProductGalleryStripContent,
  ProductGalleryStripItem,
  ProductGalleryVariant,
} from '@vs-shop/commerce-ui/productGallery';
import { Carousel, type CarouselApi } from '@vs-shop/ui/carousel';
import { Heading } from '@vs-shop/ui/heading';
import { useState } from 'react';

import { GalleryModalMainMedia } from './GalleryModalMainMedia';
import GalleryModalNextButton from './GalleryModalNextButton';
import GalleryModalPrevButton from './GalleryModalPrevButton';

type Props = {
  media: GalleryMediaT[];
  selectedIdx: number;
  setSelectedIdx: (idx: number) => void;
  productName: string;
  alt: string;
};

/**
 * The lightbox's imagery column as rcprofi arranges it: the product's name over
 * the media below `lap`, where the thumb column is not beside it to carry the
 * name, and the media itself in a looping carousel.
 */
export function GalleryModalMain({ media, selectedIdx, setSelectedIdx, productName, alt }: Props) {
  const [api, setApi] = useState<CarouselApi>();

  useCarouselSync({
    api,
    selectedIdx,
    setSelectedIdx,
  });

  return (
    <ProductGalleryLightboxMain>
      <ProductGalleryLightboxTitle className="lap:hidden" render={<Heading level={1} />}>
        {productName}
      </ProductGalleryLightboxTitle>

      <Carousel
        setApi={setApi}
        opts={{
          loop: true,
        }}
        disableKeyboardNavigation
        className="h-full w-full"
      >
        <GalleryModalPrevButton
          onPrev={() => {
            api?.scrollPrev();
          }}
        />
        <GalleryModalNextButton
          onNext={() => {
            api?.scrollNext();
          }}
        />

        <ProductGalleryLightboxViewport>
          <ProductGalleryStripContent variant={ProductGalleryVariant.OVERLAY}>
            {media.map((medium, idx) => (
              <ProductGalleryStripItem key={idx}>
                <GalleryModalMainMedia media={medium} alt={alt} isActive={idx === selectedIdx} />
              </ProductGalleryStripItem>
            ))}
          </ProductGalleryStripContent>
        </ProductGalleryLightboxViewport>
      </Carousel>
    </ProductGalleryLightboxMain>
  );
}
